import Controller from '@ember/controller';
import moment from 'moment-timezone';

import { task } from 'ember-concurrency';

import { action, get, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { cached } from '@glimmer/tracking';
import { clearAttribute } from 'beebee-base/lib/utility';
import { beebeeAjaxPost, beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import { paesiMondoAlfa3, paesiMondoAlfa2 } from 'beebee-base/lib/list_taxes';

import { later } from '@ember/runloop';

import { inject as service } from '@ember/service';
import { inject as controller } from '@ember/controller';
import { sanitizeHtml } from 'beebee-helper/helpers/safe-string';

import { debug } from '@ember/debug';

import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      controller — multi-step checkout wizard (contact form → participants →
 *              summary/riepilogo → payment), fiscal address/CF completion for foreign
 *              contacts, order confirmation against /ecommerce_confirm.
 *   extends:   Controller (@ember/controller)
 *   template:  none
 *   qp:        -
 *   key:       checkoutTask (submits /ecommerce_confirm, routes to zero-price or payment
 *              flow), confirm1stepTask (saves contact, advances step), savePartecipantiTask
 *              (validates participants, builds riepilogo), createRiepilogo
 *              (/ecommerce_freeprice), confermaPrezzo0 (/ecommerce_zero), requestPaymentTask,
 *              loadPrenoTask, checkRequired/completaStraniero (shared fiscal validation,
 *              duplicated in payment-request.js), createDataObj/addPartecipanteObj.
 *   uses:      applicationController (@controller('application') — cart, emptyCart,
 *              saveRecordRelated, gdprField), beebee-base/lib/list_taxes (paesiMondoAlfa2/3),
 *              beebee-helper safe-string sanitizeHtml, lib/utility/ajax, lib/get-config
 *   siblings:  routes/checkout.js (setupController resets step/payment/promo residuals
 *              on entry), controllers/application.js (cart owner)
 *   tests:     checkout-getters-test.js, checkout-order-test.js,
 *              checkout-request-payment-test.js, checkout-tasks-test.js,
 *              checkout-consent-gate-test.js, checkout-summary-reads-test.js,
 *              checkout-write-test.js
 *   gotchas:   `canBuy` reads GDPR consent rows via `get(d, 'active')` not `d.active` —
 *              the checkboxes write through Glimmer `mut` (= `set()`), so a native
 *              property read never revalidates the getter's tracked dependency.
 *              checkoutTask empties the cart ONLY on the pre-confirm 4xx/network branch;
 *              once /ecommerce_confirm answers the order exists, so later failures must
 *              not empty the cart or claim products are unavailable. clearItems/delCart
 *              MUST stay @action — passed unbound to <PaymentMethod> as this.args.clearItems().
 *
 * Owns the checkout wizard's step state and the two fiscal-completion/save paths
 * (contact, participants) that feed the final order confirmation call.
 */
export default class CheckoutController extends Controller {
  @controller('application') applicationController;
  @service intl;

  @service notify;
  @service session;
  @service store;
  @service log;

  @tracked step = 1;
  @tracked riepilogo = null;
  @tracked notePrenotazione = null;

  @tracked termAccepted = false;
  @tracked checkTerms = false;

  @tracked customFieldsRequired;
  @tracked paymentAPI;
  @tracked paymentZero;
  @tracked eventsReloaded;
  @tracked voucherCode;
  @tracked showPromoCode;

  @tracked _wrapContact;

  get dataUrl() {
    return getConfig(this).APP.urlData;
  }

  get not_required_field() {
    const optionField = this.session.option_field;
    return optionField?.ecommerce_not_required_field;
  }

  get safeCustomNotes() {
    const value = this.session.option_field?.ecommerce_custom_notes;
    return sanitizeHtml(value);
  }

  get contactStateAltri() {
    return this.session?._snapshotFindById?.('contact_states', '114') ?? null;
  }

  get contactStateAlbergo() {
    return this.session?._snapshotFindById?.('contact_states', '116') ?? null;
  }

  get productStateIndividuale() {
    return this.store.peekRecord('product_state', 92);
  }

  get showPartecipanti() {
    const applicationController = this.applicationController || {};
    const { ecommerceLoginBefore, cart } = applicationController;
    return !(ecommerceLoginBefore && cart?.private?.length === 0);
  }

  /*ecommerce_partecipanti_field: computed(
    'session.option_field.ecommerce_partecipanti_field',
    function () {
        return this.session.option_field?.ecommerce_partecipanti_field;
    }
  ).readOnly(),

  isCFRequired: computed(
    'ecommerce_partecipanti_field.cod_fiscale',
    function () {
        return this.ecommerce_partecipanti_field?.ecommerce_partecipanti_field?.cod_fiscale;
    }
  ).readOnly(),

  isMailRequired: computed('ecommerce_partecipanti_field.mail', function () {
    return this.ecommerce_partecipanti_field?.mail;
  }).readOnly(),*/

  get customFieldsToShow() {
    if (!this.session.me?.id) {
      return false;
    }

    const contact = this.store.peekRecord('contact', this.session.me?.id);

    if (
      Array.isArray(this.session.option_field?.custom_field_parameter) &&
      contact &&
      contact.get('organization.organization_custom_fields')
    ) {
      const ocfId = [];

      for (let i = 0; i < this.session.option_field.custom_field_parameter.length; i++) {
        const c = this.session.option_field.custom_field_parameter[i];

        if (c['ecommerce'] === true) {
          if (!c['role_id'] || c['role_id'] === contact.get('role.id')) {
            const ocf = this.store.peekRecord('organization_custom_field', c['id']);
            const cf = this.store.createRecord('custom_field', {
              organization_custom_field: ocf,
            });

            set(cf, 'required', c.required_ecom);
            ocfId.push(cf);
          }
        }
      }

      return ocfId;
    }

    return null;
  }

  get ecommerce_collettive_single() {
    return this.session.option_field?.ecommerce_collettive_single;
  }

  get generalTerms() {
    return this.session.option_field?.ecommerce_general_term;
  }

  get defMail() {
    return this.session.option_field?.default_mail;
  }

  get compDefault() {
    return this.session.option_field?.default_comp;
  }

  get canBuy() {
    const { generalTerms, termAccepted } = this;

    if (generalTerms) {
      if (termAccepted) {
        let requiredActive = true;

        const organizationTermOptions =
          this.applicationController?.gdprField?.organization_term_options?.data;

        if (Array.isArray(organizationTermOptions)) {
          organizationTermOptions.forEach(function (d) {
            // `active` MUST be read through `get`. The consent rows are plain
            // objects parsed out of `/organizations/open`; the checkbox writes
            // them with `(mut c.active)`, i.e. through Glimmer's `mut`
            // reference, which calls `set()` and so dirties
            // `tagFor(row, 'active')`. A native `d.active` read consumes no
            // tag, so this getter kept whatever value it had when
            // `termAccepted` (the only @tracked input) last changed: ticking
            // the required consent AFTER the general terms left the pay button
            // permanently disabled, and un-ticking it left the button enabled.
            // `checkin.js` still carries the pre-Octane
            // `@computed('…@each.{required,registration,active}')` that made
            // this work; the controller migration dropped it here.
            if (get(d, 'required') && get(d, 'registration') && !get(d, 'active')) {
              requiredActive = false;
            }
          });
        }

        return requiredActive;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  get isAlbergo() {
    const contactStateAlbergoId = this.contactStateAlbergo?.id;
    const contentMeContactStateId = this.session.me?.contact_state?.id;

    return contactStateAlbergoId && contactStateAlbergoId === contentMeContactStateId;
  }

  @cached
  get contact() {
    if (this._wrapContact) {
      return this._wrapContact;
    }

    const contentMeId = this.session.me?.id;
    const isAlbergo = this.isAlbergo;
    const store = this.store;

    if (contentMeId && !isAlbergo) {
      const contact = store.peekRecord('contact', contentMeId);

      if (contact) {
        contact.reload();
        return contact;
      }
    }

    return store.createRecord('contact');
  }

  set contact(value) {
    set(this, '_wrapContact', value);
  }

  checkRequired(showError = true) {
    //const contact = this.store.peekRecord('contact', this.session.me?.id);
    const contact = this.contact;
    const fieldReset = [
      'nameError',
      'ragSocialeError',
      'birthError',
      'luogoError',
      'mobileError',
      'mailError',
      'cfError',
      'codfiscaleError',
      'addressError',
    ];
    const fieldTrim = [
      'ragione_sociale',
      'first_name',
      'last_name',
      'cod_fiscale',
      'luogo_nascita',
      'mail',
      'mobile',
    ];

    // Reset error fields if showError is true
    if (showError) {
      fieldReset.forEach((field) => {
        set(contact, field, false);
      });
    }

    // Trim fields
    fieldTrim.forEach((field) => {
      const value = clearAttribute(contact.get(field));
      set(contact, field, value);
    });

    let checkField = true;
    const notRequired = this.not_required_field || {};
    const isFisicaGiuridica = contact.get('fisica_giuridica');
    const address = contact.get('addresses')[0];
    const country = address?.location_address_country;
    const countryCode = address?.location_address_country_code;

    // Check for 'ragione sociale' or name fields
    if (isFisicaGiuridica) {
      if (!contact.get('ragione_sociale')) {
        if (showError) {
          set(contact, 'ragSocialeError', true);
        }
        checkField = false;
      }
    } else {
      if (!contact.get('first_name') || !contact.get('last_name')) {
        if (showError) {
          set(contact, 'nameError', true);
        }
        checkField = false;
      }
    }

    // Check birth and place of birth fields
    if (!contact.get('data_nascita') && !notRequired.data_nascita) {
      if (showError) {
        set(contact, 'birthError', true);
      }
      checkField = false;
    }
    if (!contact.get('luogo_nascita') && !notRequired.luogo_nascita) {
      if (showError) {
        set(contact, 'luogoError', true);
      }
      checkField = false;
    }

    // Check email and mobile
    if (!contact.get('mail')) {
      if (showError) {
        set(contact, 'mailError', true);
      }
      checkField = false;
    }
    if (!contact.get('mobile') && !notRequired.mobile) {
      if (showError) {
        set(contact, 'mobileError', true);
      }
      checkField = false;
    }

    // Check address fields
    if (
      !(
        address?.location_address_street &&
        address?.location_address_house &&
        address?.location_address_postal_code &&
        address?.location_address_city &&
        address?.location_address_country
      ) &&
      !notRequired.address
    ) {
      if (showError) {
        set(contact, 'addressError', true);
      }
      checkField = false;
    }

    // Check Codice Fiscale for Italian addresses
    const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
    if (isItaly) {
      // completaStraniero() must never run for Italian contacts: it would
      // overwrite the real postal code / fiscal data with placeholder values.
      if (
        !notRequired.cod_fiscale &&
        (!contact.get('cod_fiscale') || contact.get('codFiscaleValidMsg'))
      ) {
        if (showError) {
          set(contact, 'cfError', true);
        }
        checkField = false;
      }
    } else {
      this.completaStraniero();
    }

    return checkField;
  }

  completaStraniero() {
    const item = this.contact;
    if (!item) return;
    const contactAddress = item.addresses?.[0];
    if (!contactAddress) return;
    const countryCode = contactAddress.location_address_country_code;
    const country = contactAddress.location_address_country;
    const isItaly = country === 'Italia' || ['IT', 'ITA'].includes(countryCode);
    const contactId = item.id || '';

    if (isItaly || !countryCode) {
      return;
    }

    const convertCountryCodeToAlpha2 = (countryCode) => {
      const index = paesiMondoAlfa3.indexOf(countryCode);

      if (index === -1) {
        return countryCode.substring(0, 2);
      }

      return paesiMondoAlfa2[index];
    };

    const alpha2Code = convertCountryCodeToAlpha2(countryCode);

    set(contactAddress, 'location_address_postal_code', '00000');
    set(item, 'codice_destinatario', 'XXXXXXX');

    if (item.fisica_giuridica) {
      const rimanentiCF = 14 - contactId.length;
      const contactCF = `${alpha2Code}${'00000000000000'.substring(0, rimanentiCF)}${contactId}`;
      set(item, 'cod_fiscale', contactCF);
    } else {
      set(item, 'cod_fiscale', null);
    }

    const rimanentiPIVA = 11 - contactId.length;
    const pIVA = `${alpha2Code}${'999999999'.substring(0, rimanentiPIVA)}${contactId}`;
    set(item, 'piva', pIVA);
  }

  compattaProdotti(products) {
    var productComp = [];

    if (Array.isArray(products)) {
      products.forEach(function (product) {
        if (product.product_status_id === 92) {
          let tmpProduct = productComp.filter((item) => item.id === product.id);

          if (tmpProduct.length > 0 && tmpProduct[0]?.name === product.name) {
            tmpProduct = tmpProduct[0];

            tmpProduct.events.push(...product.events);
          } else {
            const tmpProduct = product;
            set(tmpProduct, 'files', null);
            productComp.push(tmpProduct);
          }
        } else {
          const tmpProduct = product;
          set(tmpProduct, 'files', null);
          productComp.push(tmpProduct);
        }
      });
    }

    return productComp;
  }

  createAddressObj() {
    const firstAddress = this.contact?.get('addresses')[0];

    if (!firstAddress) {
      return null;
    }

    const clearAttribute = (value) => {
      if (typeof value === 'string') {
        return value.replace(/'/g, ' ');
      }
      return value;
    };

    const a = {
      id: firstAddress.id,
      attributes: {
        id: firstAddress.id,
        name: clearAttribute(firstAddress.name),
        icon: firstAddress.icon,
        view: firstAddress.view,
        place_id: firstAddress.place_id,
        location_position_lat: clearAttribute(firstAddress.location_position_lat),
        location_position_lng: clearAttribute(firstAddress.location_position_lng),
        location_address_text: clearAttribute(firstAddress.location_address_text),
        location_address_house: clearAttribute(firstAddress.location_address_house),
        location_address_street: clearAttribute(firstAddress.location_address_street),
        location_address_postal_code: clearAttribute(firstAddress.location_address_postal_code),
        location_address_city: clearAttribute(firstAddress.location_address_city),
        location_address_county: clearAttribute(firstAddress.location_address_county),
        location_address_state: clearAttribute(firstAddress.location_address_state),
        location_address_country: clearAttribute(firstAddress.location_address_country),
        location_address_country_code: clearAttribute(firstAddress.location_address_country_code),
        categories_id: firstAddress.categories_id,
        categories_title: clearAttribute(firstAddress.categories_title),
        type: firstAddress.type,
        is_invoice: firstAddress.is_invoice,
        accuracy: firstAddress.accuracy,
        heading: firstAddress.heading,
      },
    };

    for (const name in a) {
      a[name] = clearAttribute(a[name]);
    }

    return a;
  }

  @task({ drop: true }) *checkoutTask() {
    const { session, store, applicationController, intl, riepilogo, customFieldsToShow } = this;
    try {
      var cookie = document.cookie.split(';').find((item) => item.includes('beebee_ref='));
      if (cookie) {
        riepilogo.data.ref = cookie.split('=')[1];
      }
    } catch (e) {
      debug(e);
    }

    let customFieldsRequired = false;
    set(this, 'customFieldsRequired', false);

    if (Array.isArray(customFieldsToShow)) {
      riepilogo.data.custom_fields = [];
      customFieldsToShow.forEach((customField) => {
        if (customField.required) {
          if (
            ['number', 'currency', 'range'].includes(
              customField.get('organization_custom_field.data_type')
            ) &&
            (!customField.number_value || customField.number_value === '')
          ) {
            customFieldsRequired = true;
          } else if (
            customField.get('organization_custom_field.data_type') === 'checkbox' &&
            (!customField.boolean_value || customField.boolean_value === '')
          ) {
            customFieldsRequired = true;
          } else if (!customField.value || customField.value === '') {
            customFieldsRequired = true;
          }
        }

        riepilogo.data.custom_fields.push({
          organization_custom_field_id: customField.get('organization_custom_field.id'),
          value: clearAttribute(customField.value),
          number_value: clearAttribute(customField.number_value),
          boolean_value: customField.boolean_value,
        });
      });
    }

    if (customFieldsRequired) {
      set(this, 'customFieldsRequired', true);

      this.notify.warning(intl.t('missing mandatory fields'));

      return;
    }

    riepilogo.data.notes = this.notePrenotazione;
    riepilogo.data.contact.gdpr = applicationController.get(
      'gdprField.organization_term_options.data'
    );

    try {
      var cookie = document.cookie.split(';').find((item) => item.includes('beebee_ref='));

      if (cookie) {
        riepilogo.data.notes = `${
          riepilogo.data.notes ? riepilogo.data.notes : ''
        } ref: ${cookie.split('=')[1]}`;
      }
    } catch (e) {
      debug(e);
    }

    // Only the /ecommerce_confirm call itself can genuinely report that the
    // products are gone: it is the backend that re-checks availability. Once it
    // has answered, the order EXISTS — so every later failure is a client-side
    // problem and must not tell the customer their products are unavailable,
    // and must not empty the cart (the only local record of what they bought).
    let data;

    try {
      data = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_confirm`,
        session.token,
        riepilogo
      );
    } catch (e) {
      this.notify.warning(intl.t('Some products are no longer available'));

      applicationController.emptyCart();

      return;
    }

    try {
      if (typeof gtag === 'function') {
        gtag('event', 'purchase', {
          event_category: 'ecommerce',
          event_label: 'purchase',
          send_to: 'G-Q4R8QL4WC0',
        });
      }

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            transaction_id: data.payment_id,
            value: riepilogo.data.prezzo_preno,
            items: [
              ...(riepilogo.data.groups || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'groups',
                price: p.price,
                quantity: p.quantity || 1,
              })),
              ...(riepilogo.data.private || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'private',
                price: p.price,
                quantity: p.quantity || 1,
              })),
              ...(riepilogo.data.voucher || []).map((p) => ({
                item_id: p.id,
                item_name: p.name,
                item_category: 'voucher',
                price: p.price,
                quantity: p.quantity || 1,
              })),
            ],
          },
        });
      }

      if (riepilogo.data.preno_acconto_totale === 0 || riepilogo.data.prezzo_preno === 0) {
        yield this.confermaPrezzo0.perform(data.payment_id);
      } else {
        const p = yield store.findRecord('payment', data.payment_id, {
          reload: true,
        });

        set(this, 'paymentAPI', p);
        set(this, 'step', 4);

        p.project_reloaded = [];
        const pProjects = yield p.projects;
        pProjects.forEach((project) => {
          this.loadPrenoTask.perform(p, project);
        });

        later(
          this,
          function () {
            const stepEl = document.getElementById('checkOutStep3');

            if (stepEl) {
              const rect = stepEl.getBoundingClientRect();
              const top = rect.top + window.scrollY;

              window.scrollTo({ top, behavior: 'smooth' });
            }
          },
          100
        );
      }
      // applicationController.scrollBottom();
    } catch (e) {
      debug(e);

      // The order is already placed. Report a generic failure and KEEP the
      // cart: emptying it here would destroy the customer's basket over a
      // problem that has nothing to do with product availability.
      this.notify.warning(intl.t('Errors occurred while booking'));
    }
  }

  @task({ drop: true }) *loadPrenoTask(payment, project) {
    const { session, store } = this;

    set(this, 'eventsReloaded', []);
    try {
      const fullData = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/prenotazione/${project.id}`,
        session.token
      );

      store.pushPayload(fullData);
      // project_reloaded is an ad-hoc plain array rendered in checkout.hbs: reassign via set() to keep reactivity
      set(payment, 'project_reloaded', [
        ...(payment.project_reloaded || []),
        store.peekRecord('project', project.id),
      ]);
    } catch (e) {
      // Gestisci l'errore qui
    }
  }

  @task({ drop: true }) *confirm1stepTask() {
    const checkField = this.checkRequired();

    if (checkField) {
      const item = yield this.applicationController.saveRecordRelated(this.contact);
      if (!item) return;

      //this.store.pushPayload(item[0]);
      const storeItem = this.store.peekRecord('contact', item[0]?.data?.id);

      const storeAddresses = yield storeItem.addresses;
      storeAddresses
        .filter((item) => !item.id)
        .forEach((item) => {
          const _i = storeAddresses.indexOf(item);
          if (_i !== -1) storeAddresses.splice(_i, 1);
        });

      if (storeItem) {
        set(this, 'contact', storeItem);
        if (this.applicationController.ecommerceLoginBefore) {
          const related_contacts = yield storeItem.related_contacts;
          const firstContactAltri = related_contacts.find(
            (i) => i.get('contact_state.id') === this.contactStateAltri?.id
          );

          for (let i = 0; i < this.applicationController.cart.groups.length; i++) {
            this.applicationController.cart.groups[i].partecipanti = [
              {
                level: firstContactAltri?.color,
                contact: firstContactAltri,
              },
            ];
          }

          if (this.applicationController.cart.private.length > 0) {
            set(this, 'step', 2);
          } else {
            yield this.createRiepilogo.perform();
          }
        } else {
          set(this, 'step', 2);
        }
        //this.applicationController.scrollBottom();
      }
    } else {
      this.notify.warning(this.intl.t('missing mandatory fields'));
    }
  }

  @task({ drop: true }) *requestPaymentTask() {
    const { session, applicationController, intl, contact, paymentAPI, riepilogo } = this;
    const projects = [];
    let selectedContact = null;

    const paymentProjects = yield paymentAPI.projects;
    paymentProjects.forEach(function (pr) {
      projects.push(pr.id);
    });

    const paymentContacts = yield paymentAPI.contacts;
    paymentContacts.forEach((ct) => {
      if (ct.id !== this.session.me?.id) {
        selectedContact = ct;
      }
    });

    let amount = riepilogo.data.prezzo_preno;
    if (
      riepilogo.data.preno_acconto_totale != null &&
      !isNaN(parseFloat(riepilogo.data.preno_acconto_totale))
    ) {
      amount = parseFloat(riepilogo.data.preno_acconto_totale);
    }

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/request_payment`,
        session.token,
        {
          url: `https://${this.session.me?.organization?.organization}.beebeeboard.com/scuolesci_ecommerce/payment-request?`,
          organization_name: this.session.me?.organization?.name,
          object: 'Richiesta pagamento',
          message: 'Richiesta pagamento',
          contact_name: selectedContact ? selectedContact.get('name') : null,
          contact_id: selectedContact ? selectedContact.get('id') : null,
          amount: amount,
          mail: contact.mail,
          thumbnail_url: this.session.me?.organization?.thumbnail_url,
          primary_color: this.session.me?.organization?.primary_color,
          projects: projects,
        }
      );

      this.notify.success(intl.t('message sent'));
    } catch (e) {
      this.notify.error(intl.t('beebeeboard error'));
    }
  }

  @action
  emptyCart() {
    this.applicationController.emptyCart();
  }

  @action
  delCart(category = 'groups', product) {
    this.applicationController.delCart(category, product, this.applicationController);
  }

  isStepVisibleAndFilled(step) {
    const el = document.getElementById(`checkOutStep${step}`);

    if (!el) {
      return false;
    }

    return el.offsetParent !== null && el.innerText !== '';
  }

  @action
  backStep() {
    let step = this.step - 1;

    if (this.step > 1) {
      if (!this.isStepVisibleAndFilled(step - 1)) {
        step -= 1;
      }

      if (!this.isStepVisibleAndFilled(step - 1)) {
        step -= 1;
      }
    }

    if (step < 1) {
      step = 1;
    }

    set(this, 'step', step);
  }

  @task({ drop: true }) *savePartecipantiTask() {
    const { applicationController, intl } = this;
    let checkField = true;

    const ecommerce_select_level = applicationController.ecommerce_select_level;
    const all = applicationController.cart.private.concat(applicationController.cart.groups);

    all.forEach((c) => {
      if (c.partecipanti) {
        const fieldReset = [
          'notSelectError',
          'nameError',
          'ragSocialeError',
          'cfError',
          'mailError',
          'birthDateError',
          'levelError',
        ];

        c.partecipanti.forEach((p) => {
          for (let i = 0; i < fieldReset.length; i++) {
            set(p, fieldReset[i], false);
          }

          if (p.contact) {
            if (p.contact.get('fisica_giuridica')) {
              if (!p.contact.get('ragione_sociale')) {
                set(p, 'ragSocialeError', true);
                checkField = false;
              }
            } else {
              if (!p.contact.get('first_name')) {
                set(p, 'nameError', true);
                checkField = false;
              }
              if (!p.contact.get('last_name')) {
                set(p, 'nameError', true);
                checkField = false;
              }
            }

            if (!p.contact.get('data_nascita')) {
              set(p, 'birthDateError', true);
              checkField = false;
            }

            const min_age = c.item?.etminima;
            const max_age = c.item?.etmassima;

            if (
              min_age &&
              moment().diff(moment(p.contact.get('data_nascita')), 'years') < min_age
            ) {
              this.notify.warning(intl.t('Out of minimum age for this product'));
              checkField = false;
            }

            if (
              max_age &&
              moment().diff(moment(p.contact.get('data_nascita')), 'years') > max_age
            ) {
              this.notify.warning(intl.t('Out of maximum age for this product'));
              checkField = false;
            }

            if (ecommerce_select_level) {
              if (c.item.is_snow) {
                if (!p.contact.get('snow_color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              } else if (c.item.is_fondo) {
                if (!p.contact.get('fondo_color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              } else {
                if (!p.contact.get('color')) {
                  set(p, 'levelError', true);
                  checkField = false;
                }
              }
            }
          } else {
            set(p, 'notSelectError', true);
            checkField = false;
          }
        });
      } else {
        checkField = false;
      }

      if (Array.isArray(c.item?.modules_json?.moduli)) {
        c.item.modules_json.moduli.forEach((m) => {
          m.fields.forEach((f) => {
            set(f, 'fieldError', false);
            if (f.required && (!f.value || f.value === '' || f.value === ' ')) {
              set(f, 'fieldError', true);
              checkField = false;
            }
          });
        });
      }
    });

    if (checkField) {
      yield this.createRiepilogo.perform();
    } else {
      this.notify.warning(intl.t('missing mandatory fields'));
    }
  }

  @task({ drop: true }) *confermaPrezzo0(payment_id) {
    const { session, applicationController } = this;
    const me = session.me || {};

    try {
      yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_zero`,
        session.token,
        {
          payment_id,
          mail: me?.mail,
          nome: me?.name,
        }
      );

      set(this, 'step', 4);
      set(this, 'paymentZero', true);
      applicationController.emptyCart();
    } catch (e) {
      // Left uncaught, a 4xx/network failure here surfaced as a Sentry
      // unhandled rejection. User-triggered (zero-price checkout): notify.
      this.log.error('confermaPrezzo0 failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }
  }

  @task({ restartable: true }) *createRiepilogo() {
    const dataRiepilogo = this.createDataObj();

    dataRiepilogo.access_token = this.session.token;

    let riepilogo;
    try {
      riepilogo = yield beebeeAjaxPost(
        `${getConfig(this).APP.urlApi}/ecommerce_freeprice`,
        this.session.token,
        { data: dataRiepilogo }
      );
    } catch (e) {
      // Backend rejects with a 400 jqXHR ("Parametro non valido") that, left
      // uncaught, surfaced as an unhandled rejection captured as an exception
      // (Sentry EMBER-SCUOLE-ECOMMERCE-2XG). Show the message and abort.
      this.log.error('createRiepilogo failed', e);
      const detail = e?.responseJSON?.errors?.[0]?.detail;
      this.notify.error(detail || this.intl.t('beebeeboard error'));
      return;
    }

    if (!riepilogo?.data) {
      this.notify.error(this.intl.t('beebeeboard error'));
      return;
    }

    if (Array.isArray(riepilogo.data?.products)) {
      for (const product of riepilogo.data.products) {
        set(
          product,
          'partecipanti',
          typeof product.partecipanti === 'string'
            ? JSON.parse(product.partecipanti)
            : product.partecipanti
        );

        set(product, 'files', []);
        const productStore = this.store.peekRecord('product', product.id);
        if (productStore) {
          const productFiles = yield productStore.files;
          productFiles.slice().forEach((file) => {
            if (file.tags) {
              const isCert = file.tags.filter((item) => item.value === 'required');
              if (isCert.length > 0) {
                // Push into the riepilogo product's files list, not back into
                // the store relationship (which would grow it on every recalc).
                product.files.push(file);
              }
            }
          });
        }
      }
    }

    if (Array.isArray(riepilogo.data?.voucher)) {
      riepilogo.data.voucher.forEach((voucher) => {
        voucher.price_pieno = parseFloat(voucher.gift.total);
      });
    }

    if (riepilogo.data?.promo_code && riepilogo.data?.promo_code !== '') {
      if (!riepilogo.data.promo_code_valid) {
        this.notify.warning(this.intl.t('promo code not valid'));
      }
    }

    riepilogo.data.allProduct = [
      ...(riepilogo.data.groups || []),
      ...(riepilogo.data.private || []),
      ...(riepilogo.data.voucher || []),
    ];
    set(this, 'riepilogo', riepilogo);
    set(this, 'step', 3);

    later(
      this,
      () => {
        const stepEl = document.getElementById('checkOutStep2');

        if (stepEl) {
          const rect = stepEl.getBoundingClientRect();
          const top = rect.top + window.scrollY;

          window.scrollTo({ top, behavior: 'smooth' });
        }
      },
      100
    );
  }

  createDataObj() {
    const cart = {
      contact: {
        id: this.contact.get('id'),
        gdpr: this.applicationController.get('gdprField.organization_term_options.data'),
        fisica_giuridica: this.contact.get('fisica_giuridica'),
        ragione_sociale: clearAttribute(this.contact.get('ragione_sociale')),
        first_name: clearAttribute(this.contact.get('first_name')),
        last_name: clearAttribute(this.contact.get('last_name')),
        data_nascita: clearAttribute(this.contact.get('data_nascita')),
        luogo_nascita: clearAttribute(this.contact.get('luogo_nascita')),
        piva: clearAttribute(this.contact.get('piva')),
        cod_fiscale: clearAttribute(this.contact.get('cod_fiscale')),
        mail: clearAttribute(this.contact.get('mail')),
        mobile: clearAttribute(this.contact.get('mobile')),
        addresses: [],
      },
      groups: [],
      private: [],
      voucher: [...this.applicationController.cart.voucher],
      notes: this.notePrenotazione || '',
      is_albergo: this.isAlbergo,
      promo_code: clearAttribute(this.voucherCode) || '',
      custom_fields: [],
    };

    if (cart.promo_code) {
      cart.promo_code = (cart.promo_code || '').toUpperCase();
    }

    if (this.contact.get('addresses')[0]?.name) {
      cart.contact.addresses.push(this.createAddressObj());
    }

    for (var name in cart.contact) {
      cart.contact[name] = clearAttribute(cart.contact[name]);
    }

    for (let i = 0; i < this.applicationController.cart.groups.length; i++) {
      const g = this.applicationController.cart.groups[i];
      const obj = {
        comprensorio: g.description,
        data_inizio: g.start_date,
        giorni: g.giorni,
        product_id: g.product_id,
        product_status_id: g.product_status_id,
        title: g.title,
        persone: g.partecipanti?.length || 0,
        modules: g.item.modules_json,
        block_check: g.item.block_check_json,
        assicurazione: g.assicurazione,
        partecipanti: [],
      };

      this.addPartecipanteObj(g, obj);

      cart.groups.push(obj);
    }

    for (let i = 0; i < this.applicationController.cart.private.length; i++) {
      const g = this.applicationController.cart.private[i];
      const obj = {
        comprensorio: g.comprensorio,
        lingua: g.language,
        gender: g.gender,
        maestro_id: g.maestro?.id,
        product_id: g.item.id,
        product_status_id: g.item.product_status_id,
        persone: g.partecipanti?.length || 0,
        modules: g.item.modules_json,
        block_check: g.item.block_check_json,
        assicurazione: g.assicurazione,
        events: g.ore,
        partecipanti: [],
      };

      this.addPartecipanteObj(g, obj);

      cart.private.push(obj);
    }

    return cart;
  }

  addPartecipanteObj(g, obj) {
    for (let j = 0; j < g.partecipanti.length; j++) {
      const p = g.partecipanti[j];

      obj.partecipanti.push({
        nome: p.contact?.first_name,
        cognome: p.contact?.last_name,
        data_nascita: p.contact?.data_nascita,
        cod_fiscale: p.contact?.cod_fiscale,
        id: p.contact?.id,
        color: p.contact?.color,
        snow_color: p.contact?.snow_color,
        fondo_color: p.contact?.fondo_color,
      });
    }
  }

  pushPartecipanti(cart, listPartecipanti) {
    if (cart.partecipanti?.length > 0) {
      cart.partecipanti.forEach(function (e) {
        listPartecipanti.push({
          nome: e.contact?.first_name,
          cognome: e.contact?.last_name,
          cod_fiscale: e.contact?.cod_fiscale,
          mail: e.contact?.mail,
          id: e.contact?.id,
          color: e.contact?.color,
          snow_color: e.contact?.snow_color,
          fondo_color: e.contact?.fondo_color,
          number: e.number,
          data_nascita: e.contact?.data_nascita,
        });
      });
    }
  }

  // @action required: passed unbound as @clearItems to <PaymentMethod>, which
  // calls this.args.clearItems() — without binding `this` is the frozen args
  // proxy and this.applicationController is undefined (same class as delCart).
  @action
  clearItems() {
    this.applicationController.emptyCart();
  }
}
